import { EnabledLanguage, fallbackLng, languages } from './i18n-settings';

// https://stackoverflow.com/questions/1322732/convert-seconds-to-hh-mm-ss-with-javascript
export const toHHMMSS = (secs: number) => {
  const sec_num = parseInt('' + secs, 10);
  const hours = Math.floor(sec_num / 3600);
  const minutes = Math.floor(sec_num / 60) % 60;
  const seconds = sec_num % 60;

  return [hours, minutes, seconds]
    .map((v) => (v < 10 ? '0' + v : v))
    .filter((v, i) => v !== '00' || i > 0)
    .join(':');
};
export const fetchJsonAsset = async <T>(url: string): Promise<T | null> => {
  try {
    const response = await fetch(url, { cache: 'no-cache' });
    return (await response.json()) as T;
  } catch (error) {
    console.error('Issue while loading JSON:', error);
    return null;
  }
};

// https://stackoverflow.com/a/49500465
export function forceDownload(blob: string, filename: string) {
  const a = document.createElement('a');
  a.download = filename;
  a.href = blob;
  // For Firefox https://stackoverflow.com/a/32226068
  document.body.appendChild(a);
  a.click();
  a.remove();
}

// Current blob size limit is around 500MB for browsers
export function downloadResource(url: string, filename?: string) {
  if (!filename) filename = url.split('\\').pop()?.split('/').pop();
  fetch(url, {
    headers: new Headers({
      Origin: window.location.origin,
    }),
    mode: 'cors',
  })
    .then((response) => response.blob())
    .then((blob) => {
      const blobUrl = window.URL.createObjectURL(blob);
      forceDownload(blobUrl, filename ?? 'download');
    })
    .catch((e) => console.error(e));
}

export function transformKeysToCamelCase(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map((item) => transformKeysToCamelCase(item));
  } else if (obj && obj.constructor === Object) {
    const newObj: { [key: string]: any } = {};
    Object.keys(obj).forEach((key) => {
      const camelCaseKey = key.replace(/(_\w)/g, (match) =>
        (match[1] ?? '').toUpperCase(),
      );
      newObj[camelCaseKey] = transformKeysToCamelCase(obj[key]);
    });
    return newObj;
  }
  return obj;
}

export function transformKeysToSnakeCase(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map((item) => transformKeysToSnakeCase(item));
  } else if (obj && obj.constructor === Object) {
    const newObj: { [key: string]: any } = {};
    Object.keys(obj).forEach((key) => {
      const snakeCaseKey = key.replace(
        /[A-Z]/g,
        (match) => '_' + match.toLowerCase(),
      );
      newObj[snakeCaseKey] = transformKeysToSnakeCase(obj[key]);
    });
    return newObj;
  }
  return obj;
}

export const isDocustream = () => false;

// true if the color is light and false if dark
export function isBrightColor(color: string, threshold = 127.5): boolean {
  let r: number, g: number, b: number;
  const rgbMatch = color.match(
    /^rgb(a)?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*(\d+(?:\.\d+)?))?\)$/,
  );
  if (rgbMatch) {
    const [, red = 0, green = 0, blue = 0] = rgbMatch.map(Number);
    r = red;
    g = green;
    b = blue;
  } else {
    const hex = color.slice(1);
    const hexValue = parseInt(
      '0x' + (hex.length < 6 ? hex.repeat(2) : hex),
      16,
    );
    r = (hexValue >> 16) & 255;
    g = (hexValue >> 8) & 255;
    b = hexValue & 255;
  }
  const hsp = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));
  return hsp > threshold ? true : false;
}

export const getLanguageFromHeader = (acceptLanguage?: string) => {
  if (!acceptLanguage) return fallbackLng;
  const langs = acceptLanguage
    .split(',')
    .filter((language) =>
      languages.includes(language.split(';')[0]?.trim() as EnabledLanguage),
    );
  // sort by q
  langs?.sort?.((a, b) => {
    const qA = parseFloat(a.split(';')[1]?.split('=')[1] || '1');
    const qB = parseFloat(b.split(';')[1]?.split('=')[1] || '1');
    return qB - qA;
  });
  return langs?.[0]?.split(';') ?? ['en'];
};

